/**
 * balance-carrier-squadrons.ts — v3M17 carrier fighter launch constants
 * and per-race squadron chassis name lookups.
 *
 * Splits cleanly off `balance-space-structures.ts` (which holds the
 * Constructor-built space-structure constants). Squadrons aren't space
 * structures — they're mobile fighters bundled into per-race squadron
 * chassis spawned at carrier launch and despawned on recall / death.
 *
 * Names + counts that are STRUCTURAL (not balance):
 *   - `SQUADRON_CHASSIS_BY_RACE` — per-race synthetic chassis name. The
 *     loader injects a synthetic ShipTemplate at this name (mirror of
 *     `injectSpaceStructureTemplates`); the carrier's launch path
 *     spawns ShipInstances at this chassis when squadrons are deployed.
 *   - `SQUADRON_FIGHTER_COUNT` — fighters per squadron. 5 per
 *     2026-05-04 v3M17.0 design lock.
 *   - `CARRIER_SQUADRON_CAPACITY` — squadrons docked per carrier when
 *     full. 4 per design lock.
 *
 * Numbers that are BALANCE (in code, not xlsx, because they're
 * structural to the squadron model rather than tuning knobs):
 *   - `SQUADRON_REBUILD_TICKS_PER_FIGHTER` — preserves the 2026-04-21
 *     SHIPS.md §5 lock ("destroyed fighters rebuild at 1 per 100
 *     ticks"). Not an xlsx-tunable; it IS the design lock.
 *   - `SQUADRON_DEFENSE_RECALL_THRESHOLD` — `liveFighterCount` value at
 *     which `'return-at-low'` Defense Behaviour triggers a recall.
 *     Locked at 2 per the 2026-05-04 user spec. Adjusting this would be
 *     a design-lock revision, not a balance retune.
 *
 * Carrier-detection helper:
 *   - `isCarrierTemplate(tmpl)` — `tmpl.embarks.length > 0` is the
 *     authoritative carrier check across the runtime. Carriers are the
 *     only chassis with non-empty `embarks` in the catalogue (3 entries:
 *     Carrier / Heavy Carrier / Stealth Carrier in v1).
 *
 * Defaults applied at carrier construction:
 *   - `embarkedSquadronCount`: 4
 *   - `rebuildProgressFighters`: 0
 *   - `carrierAttackBehaviour`: 'visual-range'
 *   - `carrierDefenseBehaviour`: 'return-at-low'
 *
 * See `docs/v3M17-design.md` for the full design lock and `SHIPS.md §5`
 * + `MECHANICS.md §4` for the player-facing rules.
 */

import type {
  AiBehaviour,
  RaceId,
  ShipInstance,
  ShipTemplate,
} from '../state/types/index';

/**
 * Per-race synthetic squadron chassis name. Mirrors the
 * `CONSTRUCTOR_CHASSIS_BY_RACE` pattern from balance-space-structures.
 * RACE-DD / RACE-EE are v2 placeholders — they reuse the RACE-AA
 * Corvette Squadron name to keep the map total over RaceId, matching
 * the pattern in CONSTRUCTOR_CHASSIS_BY_RACE.
 */
export const SQUADRON_CHASSIS_BY_RACE: Readonly<Record<RaceId, string>> =
  Object.freeze({
    'RACE-AA': 'Corvette Squadron',
    'RACE-BB': 'Fighter Squadron',
    'RACE-CC': 'Infiltrator Squadron',
    'RACE-DD': 'Corvette Squadron',
    'RACE-EE': 'Corvette Squadron',
    // v3M21.1 — synthetic neutral empire never builds squadrons; key
    // exists only to satisfy Record<RaceId, …>. Read paths are gated
    // behind EMPIRE_NEUTRAL_ID checks.
    Neutral: '',
  });

/**
 * Parent fighter chassis name per race. Squadron template injection
 * looks up this chassis in `shipTemplates` to inherit role / loadout /
 * sublight / dodge / stealth / detection. Class 1 strike craft per
 * 2026-04-21 SHIPS.md §5.
 *
 * Note: this is duplicated here rather than derived from carrier
 * templates' `embarks[].chassis` because the embarks field has historic
 * data drift (Heavy Carrier embarks "Fighters" plural, parent chassis
 * is "Fighter" singular). Authoritative source for the squadron model
 * is this map.
 */
export const SQUADRON_PARENT_FIGHTER_BY_RACE: Readonly<Record<RaceId, string>> =
  Object.freeze({
    'RACE-AA': 'Corvette',
    'RACE-BB': 'Fighter',
    'RACE-CC': 'Infiltrator',
    'RACE-DD': 'Corvette',
    'RACE-EE': 'Corvette',
    // v3M21.1 — see SQUADRON_CHASSIS_BY_RACE comment.
    Neutral: '',
  });

/**
 * The set of all squadron chassis names. Used by UI filters that need
 * to exclude squadrons from menus (Build Ship, fleet roster, etc.) and
 * by command validators that should refuse player-issued orders on
 * squadron entities. Computed from `SQUADRON_CHASSIS_BY_RACE` values.
 */
export const SQUADRON_CHASSIS_NAMES: ReadonlySet<string> = new Set(
  Object.values(SQUADRON_CHASSIS_BY_RACE),
);

/** Number of fighters that compose one squadron. Locked at 5 per v3M17.0. */
export const SQUADRON_FIGHTER_COUNT = 5;

/** Maximum squadrons docked on a fully-stocked carrier. Locked at 4 per v3M17.0. */
export const CARRIER_SQUADRON_CAPACITY = 4;

/**
 * Ticks per fighter rebuilt. Carrier `rebuildProgressFighters`
 * accumulates 1 per this many ticks; on hitting `SQUADRON_FIGHTER_COUNT`
 * a docked squadron spawns and the accumulator resets. Preserves the
 * 2026-04-21 SHIPS.md §5 lock.
 */
export const SQUADRON_REBUILD_TICKS_PER_FIGHTER = 100;

/**
 * Per-squadron `liveFighterCount` value at which the `'return-at-low'`
 * Defense Behaviour triggers an auto-recall. Locked at 2 per the
 * 2026-05-04 user spec.
 */
export const SQUADRON_DEFENSE_RECALL_THRESHOLD = 2;

/** Default Attack Behaviour applied at carrier construction. */
export const DEFAULT_CARRIER_ATTACK_BEHAVIOUR = 'visual-range' as const;

/** Default Defense Behaviour applied at carrier construction. */
export const DEFAULT_CARRIER_DEFENSE_BEHAVIOUR = 'return-at-low' as const;

/**
 * v3M17.5 — Per-AI-behaviour-archetype defaults applied at carrier
 * construction time for AI-owned empires. Player-owned carriers use the
 * static `DEFAULT_CARRIER_*_BEHAVIOUR` constants above.
 *
 * Archetype rationale:
 *   - **Aggressive** — full lean: scan-range Attack (engage on ANY
 *     contact, including stealth ships still in scan but not vision)
 *     + no-surrender Defense (squadrons fight to the death; carrier
 *     commits everything to the fight). Matches the rusher posture.
 *   - **Balanced** — same as the player static defaults (mid-aggression
 *     visual-range + preserve-fighters return-at-low).
 *   - **Expansionist** — outward-pushing posture, but doesn't dump
 *     fighters into long battles. Visual-range + return-at-low.
 *   - **Economist** — passive on launch (none) + return-at-low so any
 *     manual launch by the AI's economy heuristic still preserves
 *     fighters.
 *   - **Technologist** — research-heavy turtle. Scan-range Attack
 *     once contact is made (the long-range sensors are paid for) +
 *     return-at-low Defense. Conservative survivability.
 *   - **Fortifier** — turtle. None + return-at-low. Squadrons launch
 *     only via manual override (currently AI never overrides — squadrons
 *     stay docked unless a future v4M0 layer issues launch commands).
 *     Matches the MFix7.5b combat-jump pattern of "Fortifier waits for
 *     defences before projecting force".
 */
export const AI_CARRIER_DEFAULTS_BY_BEHAVIOUR: Readonly<
  Record<
    AiBehaviour,
    {
      attack: 'none' | 'visual-range' | 'scan-range';
      defense: 'return-at-low' | 'no-surrender';
    }
  >
> = Object.freeze({
  // v4M8.1 — re-keyed to the new archetype IDs (AI.md §2). Carry-overs:
  // Expansionist → Expander, Aggressive → Aggressor, Fortifier → Defender,
  // Technologist → Researcher. Balanced + Economist dropped from the
  // union; Diplomat + Explorer added (defaults below match the descriptive
  // intent in AI.md §2.4 / §2.5).
  Aggressor: { attack: 'scan-range', defense: 'no-surrender' },
  Defender: { attack: 'none', defense: 'return-at-low' },
  Expander: { attack: 'visual-range', defense: 'return-at-low' },
  Explorer: { attack: 'visual-range', defense: 'return-at-low' },
  Diplomat: { attack: 'none', defense: 'return-at-low' },
  Researcher: { attack: 'scan-range', defense: 'return-at-low' },
  // v3M21.1 — synthetic neutral empire builds no carriers; key exists
  // only to satisfy Record<AiBehaviour, …>. Defaults chosen as the
  // most defensive pair so any accidental read produces benign output.
  Neutral: { attack: 'none', defense: 'return-at-low' },
});

/**
 * Authoritative carrier check. A template is a carrier if and only if
 * it embarks at least one ship type (Carrier / Heavy Carrier / Stealth
 * Carrier in v1; future content additions auto-pick up). The
 * runtime-side carrier check on a `ShipInstance` is by chassis name
 * lookup against `state.shipTemplates`.
 */
export function isCarrierTemplate(tmpl: ShipTemplate): boolean {
  return tmpl.embarks.length > 0;
}

/**
 * v3M17.3 — Recompute a squadron's `liveFighterCount` from its current
 * hull pool. Bucket-decrement: `liveFighterCount = ceil(currentHull /
 * perFighterHull)`, clamped to `[0, SQUADRON_FIGHTER_COUNT]`. Damage
 * that crosses an `(N − 1) × parent.hull` boundary drops
 * `liveFighterCount` by one; reaching 0 means every fighter is dead
 * and the combat pass's end-of-tick dead-ship cull will despawn the
 * squadron entity.
 *
 * No-op for non-squadrons (`liveFighterCount === undefined`). No-op
 * when `perFighterHull <= 0` (defensive against template drift).
 *
 * Pure: only mutates `target.liveFighterCount`. Does NOT touch
 * `currentHull` or `currentShields` — the damage chain owns those.
 * Shields are a single squadron-level pool by design; they do not
 * scale down when a fighter dies.
 *
 * Lives in this constants file rather than `advance-carrier-squadrons.ts`
 * to avoid a circular import with `advance-combat.ts` (which calls this
 * after every hull-damage round and which is itself imported by
 * `advance-carrier-squadrons.ts` for `despawnShip`).
 */
export function updateSquadronFighterCount(
  target: ShipInstance,
  perFighterHull: number,
): void {
  if (target.liveFighterCount === undefined) return;
  if (perFighterHull <= 0) return;
  const newCount = Math.ceil(target.currentHull / perFighterHull);
  target.liveFighterCount = Math.max(
    0,
    Math.min(SQUADRON_FIGHTER_COUNT, newCount),
  );
}
